import React, { ChangeEvent, FormEvent } from "react";
import { FiImage, FiSave } from "react-icons/fi";
import { LinkThreeData } from "../../types";
import utils from "../../helpers/utils";
import blobService from "../../services/blob";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

interface ProfileFormProps {
  data: LinkThreeData;
  imagePreview: string;
  loading: boolean;
  onChange: (data: LinkThreeData) => void;
  onImagePreviewChange: (preview: string) => void;
  onSave: () => Promise<void>;
  embedded?: boolean;
}

export default function ProfileForm({
  data,
  imagePreview,
  loading,
  onChange,
  onImagePreviewChange,
  onSave,
  embedded = false,
}: ProfileFormProps) {
  const { getUser } = useAuth();

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const user = getUser();
    if (!user?.id) {
      toast.error("Usuário não encontrado");
      return;
    }

    try {
      // Converter para base64
      const base64String = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Extrair apenas os dados base64 (remover o prefixo data:image/...;base64,)
      const base64Data = base64String.split(",")[1];

      // Fazer upload da imagem
      const uploadResult = await blobService.uploadImage(
        String(user.id),
        base64Data,
        file.name,
      );

      if (uploadResult) {
        // Usa a URL pré-assinada se disponível, senão usa o path
        let imagePath: string;
        if (typeof uploadResult === "object" && uploadResult.url) {
          imagePath = uploadResult.url;
        } else if (typeof uploadResult === "object" && uploadResult.path) {
          imagePath = uploadResult.path;
        } else if (typeof uploadResult === "string") {
          // Fallback para compatibilidade com versões antigas
          imagePath = `/${user.id}/${uploadResult}`;
        } else {
          imagePath = `/${user.id}/${uploadResult.file_name || ""}`;
        }

        onChange({ ...data, photo: imagePath });
        onImagePreviewChange(utils.getUriImage(imagePath));
        toast.success("Imagem carregada com sucesso!");
      } else {
        toast.error("Erro ao fazer upload da imagem");
      }
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      toast.error("Erro ao processar imagem");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await onSave();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-6 sm:gap-8 md:grid md:grid-cols-[200px_1fr] lg:grid-cols-[250px_1fr]">
        {/* Foto de Perfil */}
        <div className="flex flex-col items-center">
          <div className="mb-3 sm:mb-4">
            <label className="mb-2 block text-base font-semibold text-black dark:text-white sm:text-lg sm:mb-3">
              Foto de Perfil
            </label>
          </div>

          <div className="relative w-full max-w-[160px] sm:max-w-[180px] md:max-w-[200px]">
            {imagePreview ? (
              <div className="overflow-hidden rounded-xl border-2 border-purple-600 shadow-lg dark:border-purple-500 sm:rounded-2xl sm:border-4 sm:shadow-2xl">
                <img
                  src={imagePreview}
                  alt="Foto de perfil"
                  className="w-full h-auto object-contain"
                />
              </div>
            ) : (
              <div className="flex h-32 w-full items-center justify-center rounded-xl border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-purple-100 dark:border-purple-700 dark:from-gray-800 dark:to-gray-900 sm:rounded-2xl sm:border-4">
                <FiImage
                  className="text-purple-400 dark:text-purple-500"
                  size={50}
                />
              </div>
            )}
          </div>

          <div className="mt-4 sm:mt-6">
            <label
              htmlFor="photo-upload"
              className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 sm:px-6 sm:py-3"
            >
              <FiImage size={18} className="sm:w-5 sm:h-5" />
              Alterar Foto
            </label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Formulário */}
        <div className="space-y-4 sm:space-y-6">
          {/* Nome */}
          <div>
            <label className="mb-2 block text-sm font-medium text-black dark:text-white sm:mb-3">
              Nome
            </label>
            <input
              type="text"
              value={data.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange({ ...data, name: e.target.value })
              }
              placeholder="Seu nome completo"
              className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2.5 text-sm outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary text-black dark:text-white sm:px-5 sm:py-3"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="mb-2 block text-sm font-medium text-black dark:text-white sm:mb-3">
              Descrição / Bio
            </label>
            <textarea
              rows={4}
              value={data.description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                onChange({ ...data, description: e.target.value })
              }
              placeholder="Conte um pouco sobre você..."
              className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2.5 text-sm outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary text-black dark:text-white sm:px-5 sm:py-3"
            />
          </div>
          {/* Email (disabled) */}
          <div>
            <label className="mb-2 block text-sm font-medium text-black dark:text-white sm:mb-3">
              Email
            </label>
            <input
              type="email"
              value={getUser()?.email || ""}
              disabled
              className="w-full rounded-lg border border-stroke bg-gray-100 px-4 py-2.5 text-sm text-black outline-none dark:border-form-strokedark dark:bg-gray-800 dark:text-white sm:px-5 sm:py-3"
            />
          </div>
          {/* Botão Salvar */}
          {!embedded && (
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:opacity-50"
            >
              <FiSave size={18} className="sm:w-5 sm:h-5" />
              {loading ? "Salvando..." : "Salvar Alterações"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
